import React, { useCallback } from 'react'
import moment from 'moment'
import {
  Box,
  Flex,
  HStack,
  Icon,
  Text,
  Tooltip,
  VStack,
  AspectRatio,
  IconButton,
} from '@chakra-ui/react'
import { FaFolder } from 'react-icons/fa6'
import { FiCopy } from 'react-icons/fi'
import { Base64 } from 'js-base64'
import { useHistory } from 'react-router-dom'

import {
  MediaLibraryActions,
  MediaLibraryActionType,
} from '../../../../providers/MLProvider'
import { useTranslation } from 'react-i18next'
import { MediaLibrarySearchFolderResponse } from '../../../../interfaces'
import { useGetQueryParams } from '../../../../hooks/useGetQueryParams'
import { ContextMenuTrigger } from '../../ContextMenu'
import { useSingleClickHandling } from '../../../../hooks/useSingleClickHandling'
import ResponsiveTooltip from '../../../ResponsiveTooltip'

interface RecentlyCreatedFolderElementProps {
  focusedIndex: { index: number; type: string; id: string } | null
  setSelectedAssetMap: { [key: string]: (s: Set<string>) => void }
  selectedAssetMap: { [key: string]: Set<string> }
  setFocusedIndex: (
    x: { index: number; type: string; id: string } | null,
  ) => void
  index: number
  getIdMap: { [key: string]: (idx: number) => string }
  dispatch: React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>
  mediaCollectionRoute: boolean
  currentMediaCollection: string
  folder: MediaLibrarySearchFolderResponse
  gridTypeForDataset?: 'recent' | 'file' | 'folder'
  gridOrder: string[]
  assetCountMap: Record<string, number>
}

const RecentlyCreatedFolderElement: React.FC<RecentlyCreatedFolderElementProps> =
  React.memo(
    ({
      focusedIndex,
      setSelectedAssetMap,
      selectedAssetMap,
      setFocusedIndex,
      index,
      getIdMap,
      dispatch,
      mediaCollectionRoute,
      currentMediaCollection,
      folder,
      gridTypeForDataset,
      gridOrder,
      assetCountMap,
    }) => {
      const history = useHistory()
      const queryFromUrl = useGetQueryParams()
      const {t} = useTranslation()

      const singleClickHandler = useSingleClickHandling({
        gridOrder,
        assetCountMap,
        getIdMap,
        selectedAssetMap,
        setSelectedAssetMap,
        focusedIndex,
        setFocusedIndex,
      })

      const gridType = gridTypeForDataset ?? 'recent'

      const { name, folderPath, createdAt } = folder
      const newId = getIdMap[gridType](index)

      const isFocused =
        focusedIndex?.index === index && focusedIndex?.type === gridType
      const isSelected = !!selectedAssetMap[gridType]?.has(newId)

      const handleDoubleClick: React.MouseEventHandler<HTMLDivElement> =
        useCallback(() => {
          dispatch([
            { type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS },
            { type: MediaLibraryActions.RESET_FILE_PAGE },
            { type: MediaLibraryActions.RESET_FOLDER_PAGE },
          ])
          mediaCollectionRoute
            ? history.push(
                `/media-collections/${currentMediaCollection}/${Base64.encode(
                  folderPath,
                  true,
                )}${queryFromUrl as string}`,
              )
            : history.push(
                `/media-library/${Base64.encode(folderPath, true)}${
                  queryFromUrl as string
                }`,
              )
        }, [
          currentMediaCollection,
          dispatch,
          folderPath,
          history,
          mediaCollectionRoute,
          queryFromUrl,
        ])

      return (
        <ContextMenuTrigger
          onTrigger={event => {
            if (!isSelected) {
              dispatch([
                {
                  type: MediaLibraryActions.SELECT_FOLDER,
                  payload: { folder, event },
                },
                { type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS },
              ])
              setFocusedIndex({ index, type: gridType, id: newId })
            }
          }}
          dispatch={dispatch}
        >
          {contextMenuProps => (
            <VStack
              borderWidth="1px"
              borderRadius="6px"
              backgroundColor={isSelected ? '#e6ecf8' : '#fff'}
              borderColor={isFocused ? '#0450d5' : '#e1e4eb'}
              spacing={0}
              cursor="pointer"
              onClick={event => {
                event.stopPropagation()
                event.preventDefault()

                singleClickHandler(gridType, index, newId, event)
                dispatch([
                  { type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS },
                ])
              }}
              onDoubleClick={handleDoubleClick}
              width="100%"
              overflow="hidden"
              justifyContent="flex-start"
              alignItems="flex-start"
              position="relative"
              {...contextMenuProps}
              data-index={index}
              data-grid-type={gridType}
              className="drag-selectable-element no-right-click-context-menu"
            >
              {/* Thumbnail area (1:1) */}
              <Box
                width="100%"
                position="relative"
                padding="4px"
                borderBottomWidth="1px"
                borderColor="#e1e4eb"
              >
                <AspectRatio width="100%" ratio={1 / 1}>
                  <Flex
                    align="center"
                    justify="center"
                    width="100%"
                    height="100%"
                  >
                    <Icon
                      as={FaFolder}
                      boxSize="100px"
                      color="imagekitGray.500"
                    />
                  </Flex>
                </AspectRatio>
              </Box>

              {/* Info area */}
              <VStack
                padding="8px 16px"
                width="100%"
                spacing="4px"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <HStack
                  width="100%"
                  spacing="8px"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Tooltip label={name} placement="top-start">
                    <Text
                      maxWidth="85%"
                      noOfLines={1}
                      flexShrink={1}
                      color="#0b1b36"
                      fontWeight="600"
                      fontSize="14px"
                    >
                      {name}
                    </Text>
                  </Tooltip>

                   <ResponsiveTooltip
                  initialLabel={t('copy_name', 'Copy Name')}
                  successLabel={t('copied2', 'Copied')}
                  failureLabel={t('copy_failed', 'Failed to copy')}
                  textToCopy={name}
                >
                  <IconButton
                    padding={'0px'}
                    minWidth="unset"
                    icon={<Icon as={FiCopy} boxSize={4} />}
                    aria-label={t('copy_name', 'Copy name')}
                    color="#a0aec0"
                    variant="linkTwo"
                    size="sm"
                  />
                </ResponsiveTooltip>
                </HStack>

                <HStack spacing={0} width="full" justifyContent="space-between">
                  <Text color="appSlate" fontSize="sm" noOfLines={1}>
                    {createdAt ? `Created ${moment(createdAt).fromNow()}` : ''}
                  </Text>
                </HStack>
              </VStack>
            </VStack>
          )}
        </ContextMenuTrigger>
      )
    },
  )

export default RecentlyCreatedFolderElement
